import { useState, useEffect, useCallback } from 'react';
import { Spin, notification, Badge } from 'antd';
import { BellFilled, CheckOutlined, CloseOutlined, EnvironmentOutlined, HeartOutlined, LinkOutlined, CheckCircleOutlined, ClockCircleOutlined, UserAddOutlined } from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../../config';
import { M, G, BG } from '../shared/constants';
import { authHeader, useLang, ssGet, ssSet, fmtAmount } from '../shared/utils';
import { LastSeenBadge } from '../components/PostCard';

export function CommunityTab({ profile }) {
  const curYear = new Date().getFullYear();

  const [summary,        setSummary]        = useState(null);
  const [yearlyTop,      setYearlyTop]      = useState([]);
  const [monthlyTop,     setMonthlyTop]     = useState([]);
  const [todayPay,       setTodayPay]       = useState([]);
  const [stats,          setStats]          = useState(null);
  const [villagers,      setVillagers]      = useState([]);
  const [villageName,    setVillageName]    = useState('');
  const [sendingThanks,  setSendingThanks]  = useState({});
  const [sendingInvite,  setSendingInvite]  = useState({});
  const [sendingConnect, setSendingConnect] = useState({});
  const [thankedIds,     setThankedIds]     = useState(new Set());
  const [connectedIds,   setConnectedIds]   = useState(new Set());
  const [loadingY,       setLoadingY]       = useState(true);
  const [loadingM,       setLoadingM]       = useState(true);
  const [loadingToday,   setLoadingToday]   = useState(true);
  const [loadingStats,   setLoadingStats]   = useState(true);
  const [loadingVill,    setLoadingVill]    = useState(true);


  useEffect(() => {
    let alive = true; // prevent setState on unmounted component

    axios.get(`${baseUrl}/member/contribution-summary`, { headers: authHeader() })
      .then(r => { if (alive && r.data.status) setSummary(r.data.result); })
      .catch(() => {});

    axios.get(`${baseUrl}/member/leaderboard?period=year&year=${curYear}&limit=5`, { headers: authHeader() })
      .then(r => { if (alive && r.data.status) setYearlyTop(r.data.result || []); })
      .catch(() => {}).finally(() => { if (alive) setLoadingY(false); });

    axios.get(`${baseUrl}/member/leaderboard?period=month&year=${curYear}&limit=10`, { headers: authHeader() })
      .then(r => { if (alive && r.data.status) setMonthlyTop(r.data.result || []); })
      .catch(() => {}).finally(() => { if (alive) setLoadingM(false); });

    axios.get(`${baseUrl}/member/today-payments`, { headers: authHeader() })
      .then(r => { if (alive && r.data.status) setTodayPay(r.data.result || []); })
      .catch(() => {}).finally(() => { if (alive) setLoadingToday(false); });

    axios.get(`${baseUrl}/member/community-stats`, { headers: authHeader() })
      .then(r => { if (alive && r.data.status) setStats(r.data.result); })
      .catch(() => {}).finally(() => { if (alive) setLoadingStats(false); });

    axios.get(`${baseUrl}/member/village-members`, { headers: authHeader() })
      .then(r => { if (alive && r.data.status) { setVillagers(r.data.result || []); setVillageName(r.data.village || ''); } })
      .catch(() => {}).finally(() => { if (alive) setLoadingVill(false); });

    return () => { alive = false; };
  }, []); // eslint-disable-line

  // Load interaction states from DB on mount
  useEffect(() => {
    let alive = true;
    axios.get(`${baseUrl}/member/my-interactions`, { headers: authHeader() })
      .then(r => {
        if (alive && r.data.status) {
          setThankedIds(new Set(r.data.thanked_today || []));
          setConnectedIds(new Set(r.data.connected_ids || []));
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []); // eslint-disable-line

  const sendThanks = async (memberId) => {
    if (thankedIds.has(memberId)) return;
    setSendingThanks(s => ({ ...s, [memberId]: true }));
    try {
      await axios.post(`${baseUrl}/member/notify/thanks`, { to_user_id: memberId }, { headers: authHeader() });
      notification.success({ message: '🙏 धन्यवाद भेजा गया!' });
      setThankedIds(prev => new Set([...prev, memberId]));
    } catch (err) {
      notification.warning({ message: err.response?.data?.message || 'Could not send thanks' });
    } finally { setSendingThanks(s => ({ ...s, [memberId]: false })); }
  };

  const sendInvite = async (memberId) => {
    setSendingInvite(s => ({ ...s, [memberId]: true }));
    try {
      await axios.post(`${baseUrl}/member/notify/invite`, { to_user_id: memberId }, { headers: authHeader() });
      notification.success({ message: '🤝 आमंत्रण भेजा गया!' });
      setSendingInvite(s => ({ ...s, [memberId]: false, [memberId + '_done']: true }));
    } catch (err) {
      notification.warning({ message: err.response?.data?.message || 'Could not send invite' });
      setSendingInvite(s => ({ ...s, [memberId]: false }));
    }
  };

  const sendConnect = async (memberId) => {
    if (connectedIds.has(memberId)) return;
    setSendingConnect(s => ({ ...s, [memberId]: true }));
    try {
      const res = await axios.post(`${baseUrl}/member/contact-request`, { to_user_id: memberId }, { headers: authHeader() });
      notification.success({ message: res.data.already ? '🔗 पहले से जुड़े हैं!' : '🤝 संपर्क अनुरोध भेजा गया!' });
      setConnectedIds(prev => new Set([...prev, memberId]));
    } catch (err) {
      notification.warning({ message: err.response?.data?.message || 'Could not send request' });
    } finally { setSendingConnect(s => ({ ...s, [memberId]: false })); }
  };

  const rankStyle = (i) => {
    if (i === 0) return { bg: '#FFFBEB', border: '#FCD34D', badge: G,       text: '#92400e' };
    if (i === 1) return { bg: '#F9FAFB', border: '#C0C0C0', badge: '#C0C0C0', text: '#374151' };
    if (i === 2) return { bg: '#FFF7ED', border: '#CD7F32', badge: '#CD7F32', text: '#92400e' };
    return           { bg: '#fff',    border: '#e5e7eb', badge: '#f3f4f6', text: '#6b7280' };
  };

  const initials = (name) => (name || '?').trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const MemberRow = ({ member, i }) => {
    const r = rankStyle(i);
    return (
      <div style={{ borderRadius: 10, marginBottom: 6, background: r.bg, border: `1px solid ${r.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px' }}>
          <div style={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
            background: r.badge, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: 800, color: r.text }}>
            {i < 3 ? ['🥇','🥈','🥉'][i] : i + 1}
          </div>
          {member.photo
            ? <img src={member.photo} alt={member.name}
                style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover',
                  objectPosition: 'top', border: `2px solid ${r.border}`, flexShrink: 0 }} />
            : <div style={{ width: 36, height: 36, borderRadius: '50%', background: M, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 800, color: G }}>
                {initials(member.name)}
              </div>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{member.name}</div>
            {(member.village || member.district) && (
              <div style={{ fontSize: 10, color: '#6b7280' }}>
                <EnvironmentOutlined style={{ marginRight: 3 }} />
                {[member.village, member.district].filter(Boolean).join(', ')}
              </div>
            )}
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 900, color: M }}>₹ {fmtAmount(member.total_amount)}</div>
            <div style={{ fontSize: 9, color: '#9ca3af' }}>{member.payment_count} बार</div>
          </div>
        </div>
        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 6, padding: '0 10px 8px', justifyContent: 'flex-end' }}>
          <button
            onClick={() => sendThanks(member.id)}
            disabled={!!sendingThanks[member.id] || thankedIds.has(member.id)}
            style={{
              background: thankedIds.has(member.id) ? '#fef3c7' : '#fff',
              border: `1px solid ${thankedIds.has(member.id) ? '#f59e0b' : '#e5e7eb'}`,
              borderRadius: 6, padding: '3px 10px', fontSize: 10, fontWeight: 600,
              cursor: thankedIds.has(member.id) ? 'default' : 'pointer',
              color: thankedIds.has(member.id) ? '#92400e' : '#374151',
              display: 'flex', alignItems: 'center', gap: 3,
              opacity: sendingThanks[member.id] ? 0.6 : 1,
            }}>
            <HeartOutlined style={{ color: thankedIds.has(member.id) ? '#f59e0b' : '#ef4444' }} />
            {thankedIds.has(member.id) ? 'धन्यवाद दिया ✓' : 'धन्यवाद'}
          </button>
          <button
            onClick={() => sendConnect(member.id)}
            disabled={!!sendingConnect[member.id] || connectedIds.has(member.id)}
            style={{
              background: connectedIds.has(member.id) ? '#f0fdf4' : '#fff',
              border: `1px solid ${connectedIds.has(member.id) ? '#86efac' : M}`,
              borderRadius: 6, padding: '3px 10px', fontSize: 10, fontWeight: 600,
              cursor: connectedIds.has(member.id) ? 'default' : 'pointer',
              color: connectedIds.has(member.id) ? '#15803d' : M,
              display: 'flex', alignItems: 'center', gap: 3,
              opacity: sendingConnect[member.id] ? 0.6 : 1,
            }}>
            <LinkOutlined />
            {connectedIds.has(member.id) ? 'अनुरोध भेजा ✓' : 'संपर्क'}
          </button>
        </div>
      </div>
    );
  };

  const now       = new Date();
  const monthName = now.toLocaleString('hi-IN', { month: 'long' });
  const fyStart   = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const fyLabel   = `वित्त वर्ष ${fyStart}-${String(fyStart + 1).slice(2)}`;

  return (
    <div>
      {/* ── SAMARPAN SUMMARY — आज / माह / वर्ष ── */}
      <div style={{
        background: `linear-gradient(160deg, ${M} 0%, #b71c1c 60%, #7a0000 100%)`,
        borderRadius: 14, padding: '16px', marginBottom: 16,
        boxShadow: '0 4px 20px rgba(122,0,0,0.2)',
      }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: G, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>
          समाज शिक्षा निधि समर्पण योजना
        </div>
        {/* Today — full width, most important */}
        <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: '12px 16px', textAlign: 'center', border: '1px solid rgba(255,215,0,0.3)', marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.85)', marginBottom: 4 }}>आज का समर्पण • Today</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: G, lineHeight: 1 }}>
            {summary?.today_total != null ? `₹ ${fmtAmount(summary.today_total)}` : <Spin size="small" />}
          </div>
        </div>
        {/* Month + Year — 2 columns */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {[
            { label: `${monthName} माह`, amount: summary?.month_total, sub: 'This Month' },
            { label: fyLabel,             amount: summary?.fy_total,    sub: 'Apr – Mar' },
          ].map(({ label, amount, sub }) => (
            <div key={sub} style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 10, padding: '10px 8px', textAlign: 'center', border: '1px solid rgba(255,215,0,0.2)' }}>
              <div style={{ fontSize: 15, fontWeight: 900, color: G, lineHeight: 1 }}>
                {amount != null ? `₹ ${fmtAmount(amount)}` : <Spin size="small" />}
              </div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.85)', marginTop: 5 }}>{label}</div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>{sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── TOP 5 OF THE YEAR ── */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 14, overflow: 'hidden' }}>
        <div style={{ background: `linear-gradient(135deg, ${M}, #b71c1c)`, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: G }}>वर्ष {curYear} के शीर्ष योगदानकर्ता</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 1 }}>Top 5 Contributors of the Year</div>
          </div>
          <span style={{ fontSize: 22, color: G, fontWeight: 900 }}>5</span>
        </div>
        <div style={{ padding: '10px 12px 12px' }}>
          {loadingY
            ? <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>
            : yearlyTop.length === 0
              ? <div style={{ textAlign: 'center', padding: 14, fontSize: 12, color: '#9ca3af' }}>इस वर्ष अभी कोई डेटा नहीं</div>
              : yearlyTop.map((m, i) => <MemberRow key={m.id} member={m} i={i} />)
          }
        </div>
      </div>

      {/* ── TOP 10 THIS MONTH ── */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 14, overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>इस माह के शीर्ष 10</div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>Top 10 Contributors this Month</div>
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, background: '#fef3c7', color: '#92400e', borderRadius: 20, padding: '2px 9px' }}>
            {new Date().toLocaleString('hi-IN', { month: 'long' })}
          </span>
        </div>
        <div style={{ padding: '8px 12px 10px' }}>
          {loadingM
            ? <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>
            : monthlyTop.length === 0
              ? <div style={{ textAlign: 'center', padding: 14, fontSize: 12, color: '#9ca3af' }}>इस माह अभी कोई योगदान नहीं</div>
              : monthlyTop.map((m, i) => <MemberRow key={m.id} member={m} i={i} />)
          }
        </div>
      </div>

      {/* ── TODAY'S CONTRIBUTORS ── */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 14, overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>आज के योगदानकर्ता</div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>Today's Contributors</div>
          </div>
          {(summary?.today_count > 0 || todayPay.length > 0) && (
            <span style={{ fontSize: 11, fontWeight: 700, background: '#dcfce7', color: '#15803d', borderRadius: 20, padding: '2px 9px' }}>
              {summary?.today_count ?? todayPay.length} आज
            </span>
          )}
        </div>
        <div style={{ padding: '8px 12px 10px' }}>
          {loadingToday
            ? <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>
            : todayPay.length === 0
              ? <div style={{ textAlign: 'center', padding: '14px 0', fontSize: 12, color: '#9ca3af' }}>आज कोई योगदान नहीं आया अभी तक</div>
              : todayPay.map((p, i) => (
                <div key={i} style={{
                  borderRadius: 10, marginBottom: 6,
                  background: '#f9fafb', border: '1px solid #f0f0f0',
                  padding: '8px 10px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {p.photo
                      ? <img src={p.photo} alt={p.name}
                          onError={e => { e.target.style.display='none'; e.target.nextElementSibling.style.display='flex'; }}
                          style={{ width: 38, height: 38, borderRadius: '50%',
                            objectFit: 'cover', objectPosition: 'top', border: '2px solid #e5e7eb', flexShrink: 0 }} />
                      : null
                    }
                    <div style={{ display: p.photo ? 'none' : 'flex', width: 38, height: 38, borderRadius: '50%',
                      background: '#e5e7eb', flexShrink: 0, alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 800, color: '#6b7280' }}>
                      {initials(p.name)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{p.name}</div>
                      <div style={{ fontSize: 10, color: '#6b7280' }}>
                        {p.location && <span><EnvironmentOutlined style={{ marginRight: 2 }} />{p.location}</span>}
                        {/* {p.account_name && <span style={{ marginLeft: 6, color: '#9ca3af' }}>{p.account_name}</span>} */}
                      </div>
                    </div>
                    {Number(p.amount) >= 10000 && (
                      <div style={{ fontSize: 13, fontWeight: 900, color: '#15803d', flexShrink: 0 }}>
                        ₹ {fmtAmount(p.amount)}
                      </div>
                    )}
                  </div>
                  {(p.user_id || p.id) && (() => {
                    const uid = p.user_id || p.id;
                    return (
                    <div style={{ display: 'flex', gap: 6, marginTop: 6, justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => sendThanks(uid)}
                        disabled={!!sendingThanks[uid] || thankedIds.has(uid)}
                        style={{
                          background: thankedIds.has(uid) ? '#fef3c7' : '#fff',
                          border: `1px solid ${thankedIds.has(uid) ? '#f59e0b' : '#e5e7eb'}`,
                          borderRadius: 6, padding: '3px 10px', fontSize: 10, fontWeight: 600,
                          cursor: thankedIds.has(uid) ? 'default' : 'pointer',
                          color: thankedIds.has(uid) ? '#92400e' : '#374151',
                          display: 'flex', alignItems: 'center', gap: 3,
                          opacity: sendingThanks[uid] ? 0.6 : 1,
                        }}>
                        <HeartOutlined style={{ color: thankedIds.has(uid) ? '#f59e0b' : '#ef4444' }} />
                        {thankedIds.has(uid) ? 'धन्यवाद दिया ✓' : 'धन्यवाद'}
                      </button>
                      <button
                        onClick={() => sendConnect(uid)}
                        disabled={!!sendingConnect[uid] || connectedIds.has(uid)}
                        style={{
                          background: connectedIds.has(uid) ? '#f0fdf4' : '#fff',
                          border: `1px solid ${connectedIds.has(uid) ? '#86efac' : M}`,
                          borderRadius: 6, padding: '3px 10px', fontSize: 10, fontWeight: 600,
                          cursor: connectedIds.has(uid) ? 'default' : 'pointer',
                          color: connectedIds.has(uid) ? '#15803d' : M,
                          display: 'flex', alignItems: 'center', gap: 3,
                          opacity: sendingConnect[uid] ? 0.6 : 1,
                        }}>
                        <LinkOutlined />
                        {connectedIds.has(uid) ? 'अनुरोध भेजा ✓' : 'संपर्क'}
                      </button>
                    </div>
                    );
                  })()
                  }
                </div>
              ))
          }
        </div>
      </div>

      {/* ── VILLAGE MEMBERS ── */}
      {(loadingVill || villagers.length > 0) && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 14, overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                {villageName ? `${villageName} के सदस्य` : 'आपके गाँव के सदस्य'}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>Members from your village</div>
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, background: `${M}15`, color: M, borderRadius: 20, padding: '2px 9px' }}>
              {villagers.length}
            </span>
          </div>
          <div style={{ padding: '10px 12px', display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {loadingVill
              ? <div style={{ textAlign: 'center', padding: 20, width: '100%' }}><Spin /></div>
              : villagers.map(v => {
                // Skip deceased members (स्व. / स्वर्गीय / Late prefix)
                const isDeceased = /^(स्व\.|स्वर्गीय|Late\s)/i.test(v.name || '');
                if (isDeceased) return null;

                const isActive = v.status === 'Active';
                const hasContrib = Number(v.total_contribution) > 0;
                const alreadyInvited = !!sendingInvite[v.id + '_done'];

                return (
                  <div key={v.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 80 }}>
                    {/* Photo / initials */}
                    {v.photo
                      ? <img src={v.photo} alt={v.name}
                          onError={e => { e.target.style.display='none'; e.target.nextElementSibling.style.display='flex'; }}
                          style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top',
                            border: `2.5px solid ${isActive ? M : '#d1d5db'}` }} />
                      : null
                    }
                    <div style={{ display: v.photo ? 'none' : 'flex', width: 52, height: 52, borderRadius: '50%',
                      background: isActive ? M : '#e5e7eb',
                      alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, fontWeight: 800, color: isActive ? G : '#9ca3af' }}>
                      {initials(v.name)}
                    </div>

                    {/* Name */}
                    <div style={{ fontSize: 9.5, fontWeight: 600, color: '#374151', textAlign: 'center',
                      marginTop: 4, lineHeight: 1.3, wordBreak: 'break-word', maxWidth: 78 }}>
                      {v.name}
                    </div>
                    <LastSeenBadge ts={v.last_active_at} style={{ justifyContent: 'center', marginTop: 2 }} />

                    {/* Inactivity badge for inactive members */}
                    {!isActive && (() => {
                      const lastPay = v.last_payment_date ? new Date(v.last_payment_date) : null;
                      const monthsAgo = lastPay
                        ? Math.max(1, Math.round((Date.now() - lastPay.getTime()) / (1000 * 60 * 60 * 24 * 30)))
                        : null;
                      return (
                        <div style={{
                          fontSize: 7.5, fontWeight: 700, marginTop: 3,
                          background: '#f3f4f6', color: '#9ca3af',
                          borderRadius: 20, padding: '1px 5px',
                          textAlign: 'center', lineHeight: 1.4,
                        }}>
                          {monthsAgo ? `${monthsAgo}+ माह निष्क्रिय` : 'निष्क्रिय'}
                        </div>
                      );
                    })()}

                    {/* Action button */}
                    {!isActive ? (
                      <button
                        onClick={() => sendInvite(v.id)}
                        disabled={!!sendingInvite[v.id] || alreadyInvited}
                        style={{
                          marginTop: 5, borderRadius: 5, padding: '3px 7px', fontSize: 8.5, fontWeight: 700,
                          cursor: alreadyInvited ? 'default' : 'pointer',
                          background: alreadyInvited ? '#dcfce7' : '#fef3c7',
                          border: `1px solid ${alreadyInvited ? '#86efac' : '#fcd34d'}`,
                          color: alreadyInvited ? '#15803d' : '#92400e',
                          opacity: sendingInvite[v.id] ? 0.6 : 1,
                        }}>
                        {sendingInvite[v.id] ? '…' : alreadyInvited ? '✓ भेजा' : '🤝 आमंत्रण'}
                      </button>
                    ) : (
                      <button
                        onClick={() => sendConnect(v.id)}
                        disabled={!!sendingConnect[v.id] || connectedIds.has(v.id)}
                        style={{
                          marginTop: 5, borderRadius: 5, padding: '3px 7px', fontSize: 8.5, fontWeight: 700,
                          cursor: connectedIds.has(v.id) ? 'default' : 'pointer',
                          background: connectedIds.has(v.id) ? '#f0fdf4' : '#fff',
                          border: `1px solid ${connectedIds.has(v.id) ? '#86efac' : M}30`,
                          color: connectedIds.has(v.id) ? '#15803d' : M,
                          opacity: sendingConnect[v.id] ? 0.6 : 1,
                        }}>
                        {sendingConnect[v.id] ? '…' : connectedIds.has(v.id) ? '✓ जुड़े' : '🔗 संपर्क'}
                      </button>
                    )}
                  </div>
                );
              })
            }
          </div>
        </div>
      )}

      {/* ── COMMUNITY STATS ── */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid #f3f4f6' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>हमारा समाज</div>
          <div style={{ fontSize: 11, color: '#6b7280' }}> RAJPUROHIT Community — Member Stats</div>
        </div>
        <div style={{ padding: '12px 14px' }}>
          {loadingStats
            ? <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
            : stats && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
                  {[
                    { label: 'कुल सदस्य',  value: stats.total_members,  color: M,         sub: 'Active + Inactive' },
                    { label: 'इस माह नए', value: stats.new_this_month, color: '#15803d',  sub: 'Joined this month' },
                    ...(stats.village_count > 0 ? [{ label: stats.my_address?.village || 'गाँव', value: stats.village_count, color: '#1d4ed8', sub: 'गाँव के सदस्य' }] : []),
                    ...(stats.tehsil_count > 0  ? [{ label: stats.my_address?.tehsil  || 'तहसील', value: stats.tehsil_count, color: '#7c3aed', sub: 'तहसील के सदस्य' }] : []),
                    ...(stats.district_count > 0 ? [{ label: stats.my_address?.district || 'जिला', value: stats.district_count, color: '#0891b2', sub: 'जिले के सदस्य' }] : []),
                  ].map(({ label, value, color, sub }, i) => (
                    <div key={i} style={{ background: `${color}08`, border: `1px solid ${color}20`,
                      borderRadius: 10, padding: '12px 10px', textAlign: 'center' }}>
                      <div style={{ fontSize: 24, fontWeight: 900, color }}>{Number(value).toLocaleString('en-IN')}</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginTop: 2 }}>{label}</div>
                      <div style={{ fontSize: 9, color: '#9ca3af' }}>{sub}</div>
                    </div>
                  ))}
                </div>

                {stats.district_list?.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 8 }}>जिलेवार सदस्य संख्या</div>
                    {stats.district_list.map((d, i) => {
                      const max = stats.district_list[0]?.cnt || 1;
                      const pct = Math.round((d.cnt / max) * 100);
                      const mine = d.district && stats.my_address?.district && d.district === stats.my_address.district;
                      return (
                        <div key={i} style={{ marginBottom: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                            <span style={{ fontSize: 11, color: '#374151', fontWeight: mine ? 700 : 400 }}>
                              {d.district || '—'}
                              {mine && <span style={{ marginLeft: 5, fontSize: 9, color: M, fontWeight: 700 }}>— आपका जिला</span>}
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: M }}>{d.cnt}</span>
                          </div>
                          <div style={{ height: 5, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: mine ? M : '#d1d5db', borderRadius: 3 }} />
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </>
            )
          }
        </div>
      </div>


    </div>
  );
}


// ── Interstitial highlight cards ─────────────────────────────────────────────
export function TodayCard({ data, onSwitchTab }) {
  if (!data) return null;
  return (
    <div style={{
      background: '#fff', borderRadius: 12, border: `1px solid #e5e7eb`,
      borderLeft: `4px solid #15803d`, padding: '14px 16px', marginBottom: 14,
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>
        आज का समर्पण
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#111827' }}>
            ₹{Number(data.today_amount || 0).toLocaleString('en-IN')}
          </div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
            {data.today_count || 0} सदस्यों ने आज समर्पण किया
          </div>
        </div>
        <button
          onClick={() => onSwitchTab?.('payments')}
          style={{
            background: '#15803d', color: '#fff', border: 'none',
            borderRadius: 8, padding: '8px 16px', fontSize: 12,
            fontWeight: 700, cursor: 'pointer',
          }}>
          समर्पण करें
        </button>
      </div>
    </div>
  );
}

export function MonthlyCard({ data }) {
  if (!data) return null;
  const now = new Date();
  const monthName = now.toLocaleDateString('hi-IN', { month: 'long', year: 'numeric' });
  return (
    <div style={{
      background: '#fff', borderRadius: 12, border: `1px solid #e5e7eb`,
      borderLeft: `4px solid ${M}`, padding: '14px 16px', marginBottom: 14,
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: M, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>
        {monthName} — मासिक समर्पण
      </div>
      <div style={{ display: 'flex', gap: 24 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 900, color: '#111827' }}>
            ₹{Number(data.month_amount || 0).toLocaleString('en-IN')}
          </div>
          <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>इस माह एकत्रित</div>
        </div>
        <div>
          <div style={{ fontSize: 20, fontWeight: 900, color: '#111827' }}>
            {data.month_count || 0}
          </div>
          <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>सक्रिय समर्पक</div>
        </div>
        {data.top_name && (
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 2 }}>इस माह शीर्ष</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: M }}>{data.top_name}</div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>₹{Number(data.top_amount || 0).toLocaleString('en-IN')}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export function YearlyCard({ data }) {
  if (!data) return null;
  const fy = `${new Date().getFullYear()}-${String(new Date().getFullYear() + 1).slice(-2)}`;
  return (
    <div style={{
      background: `linear-gradient(135deg, ${M}08, ${M}04)`,
      borderRadius: 12, border: `1px solid ${M}25`,
      padding: '14px 16px', marginBottom: 14,
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: M, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
        वर्ष {fy} — वार्षिक उपलब्धि
      </div>
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'कुल समर्पण', value: `₹${Number(data.year_amount || 0).toLocaleString('en-IN')}` },
          { label: 'सक्रिय सदस्य', value: data.active_members || 0 },
        ].map(s => (
          <div key={s.label}>
            <div style={{ fontSize: 18, fontWeight: 900, color: M }}>{s.value}</div>
            <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: M, marginTop: 10, fontStyle: 'italic' }}>
        आपके समर्पण से समाज का निर्माण हो रहा है।
      </div>
    </div>
  );
}

// ── Notification Panel ───────────────────────────────────────────────────────
export function NotificationPanel({ open, onClose, onCountChange }) {
  const [items,       setItems]       = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [responding,  setResponding]  = useState({});  // { connId: true }

  const fetchNotifs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/member/notifications?limit=30`, { headers: authHeader() });
      if (res.data.status) setItems(res.data.result || []);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (open) {
      fetchNotifs();
      // mark all read after 1.5s
      setTimeout(async () => {
        try {
          await axios.patch(`${baseUrl}/member/notifications/read`, {}, { headers: authHeader() });
          onCountChange(0);
        } catch {}
      }, 1500);
    }
  }, [open]); // eslint-disable-line

  const respondConnect = async (item, action) => {
    const snap = item.sender_snapshot ? (typeof item.sender_snapshot === 'string' ? JSON.parse(item.sender_snapshot) : item.sender_snapshot) : {};
    setResponding(r => ({ ...r, [item.id]: true }));
    try {
      // find the request id from the notification's from_user_id
      const cRes = await axios.get(`${baseUrl}/member/contact-requests`, { headers: authHeader() });
      // Use == (not ===) to handle string/number type mismatch from DB
      const req = (cRes.data.result || []).find(r => Number(r.from_user_id) === Number(item.from_user_id));
      if (req) {
        await axios.patch(`${baseUrl}/member/contact-request/${req.id}`, { action }, { headers: authHeader() });
        notification.success({ message: action === 'accept' ? `${snap.name || 'सदस्य'} से संपर्क जोड़ा गया!` : 'अस्वीकार किया गया' });
        fetchNotifs();
      } else {
        notification.warning({ message: 'यह अनुरोध पहले ही संसाधित हो चुका है।', placement: 'top' });
        fetchNotifs();
      }
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'Error' });
    } finally { setResponding(r => ({ ...r, [item.id]: false })); }
  };

  const TYPE_ICON = {
    announcement:     '📢',
    payment_reminder: '💳',
    monthly_reminder: '📅',
    birthday_system:  '🌸',
    birthday_member:  '🌸',
    thanks:           '🙏',
    invite:           '🤝',
    contact_request:  '🤝',
    contact_accepted: '✅',
    general:          '🔔',
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
      }} />
      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 400,
        background: '#fff', zIndex: 1001, display: 'flex', flexDirection: 'column',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
      }}>
        {/* Header */}
        <div style={{
          background: M, color: '#fff', padding: '14px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BellFilled style={{ fontSize: 18, color: G }} />
            <span style={{ fontSize: 15, fontWeight: 800 }}>सूचनाएं</span>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
            borderRadius: '50%', width: 30, height: 30, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
          }}>×</button>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 13 }}>
              कोई सूचना नहीं है
            </div>
          ) : items.map(item => {
            const snap = item.sender_snapshot
              ? (typeof item.sender_snapshot === 'string' ? JSON.parse(item.sender_snapshot) : item.sender_snapshot)
              : null;
            const isContact = item.type === 'contact_request';
            return (
              <div key={item.id} style={{
                background: item.is_read ? '#fff' : '#fff9f0',
                border: `1px solid ${item.is_read ? '#e5e7eb' : '#fcd34d'}`,
                borderRadius: 10, padding: '10px 12px', marginBottom: 8,
              }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  {snap?.photo
                    ? <img src={snap.photo} alt={snap.name} style={{ width: 38, height: 38, borderRadius: '50%',
                        objectFit: 'cover', objectPosition: 'top', border: `2px solid ${M}`, flexShrink: 0 }} />
                    : <div style={{ width: 38, height: 38, borderRadius: '50%', background: M, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
                        {TYPE_ICON[item.type] || '🔔'}
                      </div>
                  }
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{item.title}</div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3, lineHeight: 1.5 }}>{item.message}</div>
                    {snap && (
                      <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
                        {[snap.village, snap.tahsil, snap.district].filter(Boolean).join(', ')}
                      </div>
                    )}
                    <div style={{ fontSize: 9.5, color: '#c4b5a0', marginTop: 4 }}>
                      {item.received_at ? new Date(item.received_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                    </div>

                    {isContact && (
                      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                        <button onClick={() => respondConnect(item, 'accept')}
                          disabled={!!responding[item.id]}
                          style={{
                            background: '#15803d', color: '#fff', border: 'none', borderRadius: 6,
                            padding: '4px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 4, opacity: responding[item.id] ? 0.6 : 1,
                          }}>
                          <CheckOutlined /> स्वीकार करें
                        </button>
                        <button onClick={() => respondConnect(item, 'decline')}
                          disabled={!!responding[item.id]}
                          style={{
                            background: '#fff', color: '#dc2626', border: '1px solid #dc2626', borderRadius: 6,
                            padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 4, opacity: responding[item.id] ? 0.6 : 1,
                          }}>
                          <CloseOutlined /> अस्वीकार
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

